import { AxiosResponse } from "axios";
import { createAxiosInstance } from "./AxiosService";

const axiosInstance = createAxiosInstance();

// Pagination interfaces
export interface PaginationParams {
  page?: number;
  per_page?: number;
  search?: string;
}

export interface PaginatedResponse<T> {
  data?: T[];
  emails?: T[];
  profile_photos?: T[];
  roles?: T[];
  role_contexts?: T[];
  total?: number;
  pagination: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
  };
}

// Entity interfaces
export interface Email {
  id: string;
  email: string;
  created_at: string;
  updated_at: string;
}

export interface ProfilePhoto {
  id: string;
  thumbnail: string;
  profile: string;
  created_at: string;
  updated_at: string;
}

export interface Role {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface RoleContext {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

// Request interfaces
export interface CreateEmailRequest {
  email: string;
}

export interface UpdateEmailRequest {
  email?: string;
}

export interface CreateProfilePhotoRequest {
  thumbnail: string;
  profile: string;
}

export interface UpdateProfilePhotoRequest {
  thumbnail?: string;
  profile?: string;
}

export interface CreateRoleRequest {
  name: string;
}

export interface UpdateRoleRequest {
  name?: string;
}

export interface CreateRoleContextRequest {
  name: string;
}

export interface UpdateRoleContextRequest {
  name?: string;
}

class GenericService {
  // Email endpoints
  static async getEmails(
    params?: PaginationParams,
  ): Promise<PaginatedResponse<Email>> {
    const response: AxiosResponse<PaginatedResponse<Email>> =
      await axiosInstance.get("/generic/emails", { params });
    return response.data;
  }

  static async getEmailById(id: string): Promise<Email> {
    const response: AxiosResponse<Email> = await axiosInstance.get(
      `/generic/emails/${id}`,
    );
    return response.data;
  }

  static async getEmailByEmail(email: string): Promise<Email> {
    const response: AxiosResponse<Email> = await axiosInstance.get(
      `/generic/emails/by_email`,
      { params: { email } },
    );
    return response.data;
  }

  static async createEmail(data: CreateEmailRequest): Promise<Email> {
    const response: AxiosResponse<Email> = await axiosInstance.post(
      "/generic/emails",
      data,
    );
    return response.data;
  }

  static async updateEmail(
    id: string,
    data: UpdateEmailRequest,
  ): Promise<Email> {
    const response: AxiosResponse<Email> = await axiosInstance.put(
      `/generic/emails/${id}`,
      data,
    );
    return response.data;
  }

  static async deleteEmail(id: string): Promise<void> {
    await axiosInstance.delete(`/generic/emails/${id}`);
  }

  // ProfilePhoto endpoints
  static async getProfilePhotos(
    params?: PaginationParams,
  ): Promise<PaginatedResponse<ProfilePhoto>> {
    const response: AxiosResponse<PaginatedResponse<ProfilePhoto>> =
      await axiosInstance.get("/generic/profile_photos", { params });
    return response.data;
  }

  static async getProfilePhotoById(id: string): Promise<ProfilePhoto> {
    const response: AxiosResponse<ProfilePhoto> = await axiosInstance.get(
      `/generic/profile_photos/${id}`,
    );
    return response.data;
  }

  static async createProfilePhoto(
    data: CreateProfilePhotoRequest,
  ): Promise<ProfilePhoto> {
    const response: AxiosResponse<ProfilePhoto> = await axiosInstance.post(
      "/generic/profile_photos",
      data,
    );
    return response.data;
  }

  static async updateProfilePhoto(
    id: string,
    data: UpdateProfilePhotoRequest,
  ): Promise<ProfilePhoto> {
    const response: AxiosResponse<ProfilePhoto> = await axiosInstance.put(
      `/generic/profile_photos/${id}`,
      data,
    );
    return response.data;
  }

  static async deleteProfilePhoto(id: string): Promise<void> {
    await axiosInstance.delete(`/generic/profile_photos/${id}`);
  }

  // Role endpoints
  static async getRoles(
    params?: PaginationParams,
  ): Promise<PaginatedResponse<Role>> {
    const response: AxiosResponse<PaginatedResponse<Role>> =
      await axiosInstance.get("/generic/roles", { params });
    return response.data;
  }

  static async getRoleById(id: string): Promise<Role> {
    const response: AxiosResponse<Role> = await axiosInstance.get(
      `/generic/roles/${id}`,
    );
    return response.data;
  }

  static async createRole(data: CreateRoleRequest): Promise<Role> {
    const response: AxiosResponse<Role> = await axiosInstance.post(
      "/generic/roles",
      data,
    );
    return response.data;
  }

  static async updateRole(id: string, data: UpdateRoleRequest): Promise<Role> {
    const response: AxiosResponse<Role> = await axiosInstance.put(
      `/generic/roles/${id}`,
      data,
    );
    return response.data;
  }

  static async deleteRole(id: string): Promise<void> {
    await axiosInstance.delete(`/generic/roles/${id}`);
  }

  // RoleContext endpoints
  static async getRoleContexts(
    params?: PaginationParams,
  ): Promise<PaginatedResponse<RoleContext>> {
    const response: AxiosResponse<PaginatedResponse<RoleContext>> =
      await axiosInstance.get("/generic/role_contexts", { params });
    return response.data;
  }

  static async getRoleContextById(id: string): Promise<RoleContext> {
    const response: AxiosResponse<RoleContext> = await axiosInstance.get(
      `/generic/role_contexts/${id}`,
    );
    return response.data;
  }

  static async createRoleContext(
    data: CreateRoleContextRequest,
  ): Promise<RoleContext> {
    const response: AxiosResponse<RoleContext> = await axiosInstance.post(
      "/generic/role_contexts",
      data,
    );
    return response.data;
  }

  static async updateRoleContext(
    id: string,
    data: UpdateRoleContextRequest,
  ): Promise<RoleContext> {
    const response: AxiosResponse<RoleContext> = await axiosInstance.put(
      `/generic/role_contexts/${id}`,
      data,
    );
    return response.data;
  }

  static async deleteRoleContext(id: string): Promise<void> {
    await axiosInstance.delete(`/generic/role_contexts/${id}`);
  }

  // Service availability check
  static isAvailable(): boolean {
    return true;
  }
}

export default GenericService;
